import type { RadioGroupProps } from '@radix-ui/react-radio-group';
import type { FC, ReactNode } from 'react';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { Flex } from '@ui';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useId } from 'react';
import styles from './RadioGroup.module.css';

type Item = {
    label?: ReactNode;
    value: string;
    disabled?: boolean;
    required?: boolean;
};

type Props = {
    vertical?: boolean;
    items: Item[];
    onChange?: RadioGroupProps['onValueChange'];
    error?: boolean;
} & Omit<RadioGroupProps, 'onValueChange' | 'onChange' | 'className' | 'style'>;

const RadioItem: FC<{ item: Item }> = ({ item }) => {
    const { label, value, required, disabled } = item;
    const id = useId();
    const itemElement = (
        <RadixRadioGroup.Item
            asChild
            required={required}
            disabled={disabled}
            value={value}
            className={styles.radioItem}
        >
            <motion.button
                id={label ? id : undefined}
                initial={{ scale: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.9 }}
                transition={{
                    type: 'spring',
                    stiffness: 1000,
                    damping: 30
                }}
            >
                <RadixRadioGroup.Indicator asChild className={styles.indicator}>
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                            type: 'spring',
                            stiffness: 1000,
                            damping: 30
                        }}
                    />
                </RadixRadioGroup.Indicator>
            </motion.button>
        </RadixRadioGroup.Item>
    );
    if (!label)
        return itemElement;
    return (
        <Flex gap={12} align="center">
            {itemElement}
            <label htmlFor={id} className={styles.label}>{label}</label>
        </Flex>
    );
};

export const RadioGroup: FC<Props> = ({ vertical, items, value, onChange, error, ...props }) => (
    <RadixRadioGroup.Root
        value={value}
        onValueChange={onChange}
        className={clsx(styles.radioGroup, vertical && styles.vertical)}
        {...props}
    >
        {items.map(item => <RadioItem key={item.value} item={item} />)}
    </RadixRadioGroup.Root>
);
